import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_LANGUAGE } from "@base/language/lang";
import authService from "@base/modules/auth/authService";

const BRIDGE_MESSAGE_SOURCE = "medihome-turnstile-widget";

// The widget page only reports to the mobile apps (AndroidBridge / webkit handlers), so it gets a
// fake AndroidBridge that forwards each `{ type, token | errorCode }` to this window instead.
const BRIDGE_SCRIPT =
  "<script>window.AndroidBridge={onMessage:function(json){window.parent.postMessage(" +
  `{source:"${BRIDGE_MESSAGE_SOURCE}",payload:JSON.parse(json)},"*")}};</script>`;

/** Right after `<head>`, so the bridge exists before the page's own script can call it. */
const injectBridge = (html: string) => {
  const head = /<head[^>]*>/i;

  return head.test(html) ? html.replace(head, (tag) => tag + BRIDGE_SCRIPT) : BRIDGE_SCRIPT + html;
};

type IWidgetMessage = { type: "success"; token: string } | { type: "error"; errorCode?: string } | { type: "expired" };

const useTurnstile = () => {
  // A callback ref, not a ref object: a page whose widget moves to another element (each step of a
  // wizard renders its own) gets a new iframe, and only state can re-run the listener effect for it.
  const [iframe, setIframe] = useState<HTMLIFrameElement | null>(null);
  // Mirrors `iframe` for resetWidget, which callers run after an await - by then a wizard may have
  // moved on to the next step's iframe, and a closed-over one would reset the old widget.
  const currentIframeRef = useRef<HTMLIFrameElement | null>(null);
  const iframeRef = useCallback((node: HTMLIFrameElement | null) => {
    currentIframeRef.current = node;
    setIframe(node);
  }, []);
  const [widgetHtml, setWidgetHtml] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const { i18n } = useTranslation();

  const language = i18n.resolvedLanguage ?? DEFAULT_LANGUAGE;

  // The language is baked into the page the server returns, so switching it means fetching a new one.
  useEffect(() => {
    let isStale = false;

    setWidgetHtml(null);
    setToken(null);

    authService.getTurnstileWidget(language).then(
      (html) => {
        if (!isStale) {
          setWidgetHtml(injectBridge(html));
          setHasError(false);
        }
      },
      () => {
        // Nothing to solve without the page, so the submit stays locked behind the error.
        if (!isStale) {
          setHasError(true);
        }
      },
    );

    return () => {
      isStale = true;
    };
  }, [language]);

  useEffect(() => {
    if (!iframe) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      const data = event.data;

      if (event.source !== iframe.contentWindow || data?.source !== BRIDGE_MESSAGE_SOURCE || !data.payload) {
        return;
      }

      const payload = data.payload as IWidgetMessage;

      if (payload.type === "success") {
        setToken(payload.token);
        setHasError(false);
      } else if (payload.type === "error") {
        // The page has already retried on its own, so this one is final.
        setToken(null);
        setHasError(true);
      } else if (payload.type === "expired") {
        setToken(null);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
      // Its token belonged to the iframe just dropped.
      setToken(null);
    };
  }, [iframe]);

  const resetWidget = useCallback(() => {
    // A srcdoc iframe shares this origin, so its turnstile is reachable - once the script has loaded.
    try {
      (currentIframeRef.current?.contentWindow as (Window & { turnstile?: { reset: () => void } }) | null)?.turnstile?.reset();
    } catch {
      // Not loaded yet: there is no token to throw away either.
    }
    setToken(null);
  }, []);

  // No `sandbox`: Turnstile needs the page's real hostname, which an opaque origin would hide. The
  // server controls this HTML, so running it on our origin is accepted.
  const widget = (
    <div className="w-full max-w-full min-w-[300px] min-h-[65px]">
      {widgetHtml ? <iframe ref={iframeRef} title="CAPTCHA" srcDoc={widgetHtml} scrolling="no" className="block w-full h-[65px] border-0" /> : null}
    </div>
  );

  return {
    widget,
    token,
    hasError,
    resetWidget,
  };
};

export default useTurnstile;
